import { useState, useEffect } from 'react';
import {
  Wallet, CheckCircle, Copy, Loader2, X, ExternalLink,
  MessageCircle, MapPin, Package, ChevronDown, Info, AlertCircle
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import instapayQR from '@/assets/islam-zaky-cairo-bank-instapay-qr.jpeg';
import whatsappQR from '@/assets/zm-whatsapp.jpeg';

// ── Shipping zones (used in CartDrawer + checkout) ───────────
export const SHIPPING_ZONES: { label: string; cities: string[]; fee: number }[] = [
  { label: 'Cairo, Giza, Alexandria & Behira', cities: ['Cairo', 'Giza', 'Alexandria', 'Behira', 'Damanhur'], fee: 100 },
  { label: 'Delta & Canal', cities: ['Dakahlia', 'El Kalioubia', 'Gharbia', 'Kafr Alsheikh', 'Monufia', 'Sharqia', 'Damietta', 'Ismailia', 'Port Said', 'Suez', 'Mansoura', 'Tanta', 'Zagazig'], fee: 110 },
  { label: 'Near Upper Egypt', cities: ['Fayoum', 'Beni Suef', 'Minya', 'Asyut', 'Sohag'], fee: 125 },
  { label: 'Far Upper Egypt & Matrouh', cities: ['Qena', 'Luxor', 'Aswan', 'Red Sea', 'Matrouh', 'Hurghada'], fee: 145 },
  { label: 'North Coast', cities: ['North Coast'], fee: 150 },
  { label: 'Sinai & New Valley', cities: ['North Sinai', 'South Sinai', 'New Valley'], fee: 165 },
];

export const getShippingFee = (city: string): number =>
  SHIPPING_ZONES.find(z => z.cities.some(c => c.toLowerCase() === city.toLowerCase()))?.fee ?? 100;

export const OPEN_BEFORE_PICKUP_FEE = 10;
export const HIGH_VALUE_THRESHOLD = 1500;

// ── ShippingCalculator — drop into CartDrawer ────────────────
interface ShippingCalculatorProps {
  orderTotal: number;
  selectedCity: string;
  openBeforePickup: boolean;
  allowOpenBeforePickup: boolean; // admin-controlled per product
  onCityChange: (city: string) => void;
  onOpenBeforePickupChange: (val: boolean) => void;
  shippingFee: number;
}

export function ShippingCalculator({
  orderTotal, selectedCity, openBeforePickup, allowOpenBeforePickup,
  onCityChange, onOpenBeforePickupChange, shippingFee,
}: ShippingCalculatorProps) {
  const isHighValue = orderTotal >= HIGH_VALUE_THRESHOLD;
  const openFee = openBeforePickup ? OPEN_BEFORE_PICKUP_FEE : 0;

  return (
    <div className="space-y-4 p-4 rounded-xl border border-border bg-secondary/10">
      <p className="text-sm font-semibold flex items-center gap-2">
        <MapPin className="h-4 w-4 text-primary" /> Delivery Details
      </p>

      {/* City selector */}
      <div className="space-y-1.5">
        <label className="text-xs font-medium text-muted-foreground">City / Governorate</label>
        <div className="relative">
          <select value={selectedCity} onChange={e => onCityChange(e.target.value)}
            className="w-full appearance-none bg-background border border-border rounded-lg px-3 py-2.5 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary/50 pr-8">
            <option value="">Select your city...</option>
            {SHIPPING_ZONES.map(zone => (
              <optgroup key={zone.label} label={`${zone.label} — ${zone.fee} EGP`}>
                {zone.cities.map(city => <option key={city} value={city}>{city}</option>)}
              </optgroup>
            ))}
          </select>
          <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
        </div>
        {selectedCity && (
          <p className="text-xs text-primary font-medium">
            Shipping: <span className="font-bold">{shippingFee} EGP</span>
            {' — '}{SHIPPING_ZONES.find(z => z.cities.some(c => c.toLowerCase() === selectedCity.toLowerCase()))?.label}
          </p>
        )}
      </div>

      {/* Open before pickup — only shown if admin enabled it for these products */}
      {allowOpenBeforePickup && (
        <label className="flex items-start gap-3 cursor-pointer group">
          <div className="relative mt-0.5 shrink-0">
            <input type="checkbox" checked={openBeforePickup} onChange={e => onOpenBeforePickupChange(e.target.checked)} className="sr-only" />
            <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${openBeforePickup ? 'bg-primary border-primary' : 'border-border group-hover:border-primary/50'}`}>
              {openBeforePickup && (
                <svg className="w-3 h-3 text-primary-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              )}
            </div>
          </div>
          <div className="flex-1">
            <p className="text-sm font-medium leading-snug">
              Open package before pickup
              <span className="ml-2 text-xs font-semibold px-1.5 py-0.5 rounded-full bg-amber-500/20 text-amber-400 border border-amber-500/30">
                +{OPEN_BEFORE_PICKUP_FEE} EGP
              </span>
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Allow Bosta courier to open the package at your door for inspection before accepting delivery.
            </p>
          </div>
        </label>
      )}

      {/* High value notice */}
      {isHighValue && (
        <div className="flex items-start gap-2 p-3 rounded-lg bg-violet-500/10 border border-violet-500/20 text-xs text-violet-300">
          <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
          <span><span className="font-semibold">High-value order (above EGP 1,500)</span> — InstaPay or Card payment required. Cash on Delivery is not available.</span>
        </div>
      )}

      {/* Fee summary */}
      {selectedCity && (
        <div className="pt-3 border-t border-border space-y-1.5 text-sm">
          <div className="flex justify-between text-muted-foreground">
            <span>Order subtotal</span><span>EGP {orderTotal.toLocaleString()}</span>
          </div>
          <div className="flex justify-between text-muted-foreground">
            <span>Shipping</span><span>EGP {shippingFee}</span>
          </div>
          {openBeforePickup && allowOpenBeforePickup && (
            <div className="flex justify-between text-amber-400">
              <span>Open before pickup</span><span>+EGP {OPEN_BEFORE_PICKUP_FEE}</span>
            </div>
          )}
          <div className="flex justify-between font-bold text-base pt-1.5 border-t border-border">
            <span>Total</span>
            <span className="text-primary">EGP {(orderTotal + shippingFee + openFee).toLocaleString()}</span>
          </div>
        </div>
      )}
    </div>
  );
}

// ── InstaPay Checkout modal/panel ────────────────────────────
interface InstapayCheckoutProps {
  orderNumber: string;
  orderData?: Record<string, unknown> | null;
  orderTotal: number;
  shippingFee?: number;
  onClose?: () => void;
  onConfirmed?: () => void;
}

export default function InstapayCheckout({
  orderNumber, orderData, orderTotal, shippingFee = 0, onClose, onConfirmed,
}: InstapayCheckoutProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [confirmed, setConfirmed] = useState(false);
  const [copied, setCopied] = useState<'number' | 'link' | null>(null);
  const [settings, setSettings] = useState({ number: '01037530503', name: 'Islam Sayed Zaky', bank: 'Mashreq Egypt' });
  const totalAmount = orderTotal + shippingFee;

  useEffect(() => {
    const load = async () => {
      const { data } = await supabase.from('app_settings_public').select('key, value')
        .in('key', ['instapay_number', 'instapay_name', 'instapay_bank']);
      if (data) {
        const map = Object.fromEntries(data.map(r => [r.key, r.value || '']));
        setSettings({ number: map.instapay_number || '01037530503', name: map.instapay_name || 'Islam Sayed Zaky', bank: map.instapay_bank || 'Mashreq Egypt' });
      }
      setIsLoading(false);
    };
    load();
  }, []);

  const copy = (text: string, type: 'number' | 'link') => {
    navigator.clipboard.writeText(text);
    setCopied(type);
    setTimeout(() => setCopied(null), 2500);
  };

  const handleConfirmed = async () => {
    await supabase.from('orders').update({ payment_method: 'instapay', status: 'pending_verification' }).eq('order_number', orderNumber);
    // Fire notification now that customer has confirmed payment — reuses the
    // payload already in memory from checkout rather than re-selecting the
    // order (which holds PII) from the database.
    try {
      if (orderData) {
        const order = { ...orderData, payment_method: 'instapay', status: 'pending_verification' };
        supabase.functions.invoke('order-notify', { body: { order } }).catch(() => {});
      }
    } catch (_) {}
    setConfirmed(true);
    onConfirmed?.();
  };

  if (isLoading) return <div className="flex items-center justify-center p-12"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  /* ── Success state ── */
  if (confirmed) {
    return (
      <div className="flex flex-col items-center gap-5 p-6 text-center">
        <div className="h-16 w-16 rounded-full bg-emerald-500/20 flex items-center justify-center">
          <CheckCircle className="h-9 w-9 text-emerald-400" />
        </div>
        <div className="space-y-1.5 max-w-xs">
          <h3 className="text-lg font-bold">Order Confirmed!</h3>
          <p className="text-sm text-muted-foreground">Order <span className="font-mono font-bold text-foreground">#{orderNumber}</span> is <span className="text-amber-400 font-semibold">Processing</span>.</p>
          <p className="text-sm text-muted-foreground">Our team will verify your transfer and confirm on WhatsApp within <span className="font-semibold text-foreground">1 hour</span>.</p>
        </div>
        <div className="w-full space-y-3">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Next — send your screenshot here:</p>
          <a href="https://wa.me/message/ACL5MCRFCEGOD1" target="_blank" rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 w-full px-5 py-3 rounded-xl bg-[#25D366] text-black text-sm font-bold hover:bg-[#1ebe5a] transition-colors">
            <MessageCircle className="h-4 w-4" /> Send Screenshot on WhatsApp
          </a>
          <p className="text-xs text-muted-foreground">WhatsApp: <span className="font-mono font-semibold text-foreground">+20 1037530503</span></p>
        </div>
        {onClose && (
        <div className="text-center space-y-2">
          <p className="text-xs text-emerald-400 font-medium">✅ We will verify and confirm your shipment within 1 hour.</p>
          <button onClick={onClose} className="text-xs text-muted-foreground hover:text-foreground underline">Close & continue shopping</button>
        </div>
      )}
      </div>
    );
  }

  /* ── Main payment screen ── */
  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between px-5 pt-5">
        <div className="flex items-center gap-2.5">
          <div className="h-9 w-9 rounded-full bg-violet-500/20 flex items-center justify-center">
            <Wallet className="h-4 w-4 text-violet-400" />
          </div>
          <div>
            <h3 className="font-bold text-base leading-tight">Pay via InstaPay</h3>
            <p className="text-xs text-muted-foreground">Order #{orderNumber}</p>
          </div>
        </div>
        {onClose && (
          <button onClick={onClose} aria-label="Close" className="p-1.5 text-muted-foreground hover:text-foreground rounded-md hover:bg-secondary transition-colors">
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      <div className="px-5 pb-6 space-y-5">
        {/* Amount */}
        <div className="p-4 rounded-2xl bg-primary/5 border border-primary/20 text-center space-y-1">
          <p className="text-xs text-muted-foreground">Transfer exactly</p>
          <p className="text-4xl font-bold text-primary tracking-tight">EGP {totalAmount.toLocaleString()}</p>
          {shippingFee > 0 && <p className="text-xs text-muted-foreground">Order EGP {orderTotal.toLocaleString()} + Shipping EGP {shippingFee}</p>}
          <p className="text-xs text-amber-400 font-semibold pt-1">Write <span className="font-mono">{orderNumber}</span> as the transfer note</p>
        </div>

        {/* QR + Account */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="flex flex-col items-center gap-2 p-4 rounded-xl border border-border bg-white">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Scan QR to Pay</p>
            <img src={instapayQR} alt="InstaPay QR Code" className="w-36 h-36 object-contain rounded-lg" />
            <a href="https://ipn.eg/S/is.zaky/instapay/1Fi7NY" target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-1.5 text-xs font-semibold text-violet-600 hover:text-violet-700 transition-colors mt-1">
              <ExternalLink className="h-3.5 w-3.5" /> Click to pay directly
            </a>
            <button onClick={() => copy('https://ipn.eg/S/is.zaky/instapay/1Fi7NY', 'link')}
              className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors">
              {copied === 'link' ? <CheckCircle className="h-3 w-3 text-emerald-400" /> : <Copy className="h-3 w-3" />}
              {copied === 'link' ? 'Copied!' : 'Copy link'}
            </button>
          </div>
          <div className="space-y-3 p-4 rounded-xl border border-border bg-secondary/20">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Account Details</p>
            <div className="space-y-2.5">
              <div>
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Phone Number</p>
                <div className="flex items-center justify-between gap-1">
                  <p className="text-base font-bold font-mono">{settings.number}</p>
                  <button onClick={() => copy(settings.number, 'number')}
                    className="shrink-0 flex items-center gap-1 px-2 py-1 rounded-lg border border-border text-[10px] hover:bg-secondary transition-colors">
                    {copied === 'number' ? <CheckCircle className="h-3 w-3 text-emerald-400" /> : <Copy className="h-3 w-3" />}
                    {copied === 'number' ? 'Copied!' : 'Copy'}
                  </button>
                </div>
              </div>
              <div>
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Name</p>
                <p className="text-sm font-semibold">{settings.name}</p>
              </div>
              <div>
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Bank</p>
                <p className="text-sm font-semibold">{settings.bank}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Instructions */}
        <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/20 space-y-2.5">
          <p className="text-xs font-semibold text-amber-400 uppercase tracking-wider flex items-center gap-1.5">
            <Info className="h-3.5 w-3.5" /> How to complete payment
          </p>
          <ol className="text-xs text-amber-200/90 space-y-1.5 list-decimal list-inside leading-relaxed">
            <li>Open InstaPay app and scan the QR — or click the payment link above</li>
            <li>Transfer exactly <span className="font-bold">EGP {totalAmount.toLocaleString()}</span></li>
            <li>Enter <span className="font-mono font-bold">{orderNumber}</span> as the transfer note/reference</li>
            <li>Take a screenshot of the <span className="font-bold">successful transfer status</span> from InstaPay app</li>
            <li>Send screenshot to our WhatsApp: <span className="font-mono font-bold">+20 1037530503</span></li>
          </ol>
        </div>

        {/* Processing notice */}
        <div className="flex items-start gap-2.5 p-3 rounded-xl bg-blue-500/10 border border-blue-500/20 text-xs text-blue-300">
          <Package className="h-4 w-4 shrink-0 mt-0.5" />
          <span>Your order stays in <span className="font-semibold text-blue-200">Processing</span> status until our team verifies the transfer. We'll confirm and proceed with shipment via WhatsApp within <span className="font-semibold text-blue-200">1 hour</span>.</span>
        </div>

        {/* WhatsApp */}
        <div className="p-4 rounded-xl border border-[#25D366]/30 bg-[#25D366]/5 space-y-3">
          <p className="text-sm font-semibold text-[#25D366] flex items-center gap-2">
            <MessageCircle className="h-4 w-4" /> Send Your Screenshot Here
          </p>
          <div className="flex flex-col sm:flex-row gap-4 items-center">
            <div className="flex flex-col items-center gap-1.5 p-3 bg-white rounded-xl border border-gray-100 shrink-0">
              <img src={whatsappQR} alt="WhatsApp QR" className="w-24 h-24 object-contain rounded-lg" />
              <p className="text-[10px] text-gray-400">Scan to open</p>
            </div>
            <div className="flex-1 space-y-2.5 text-center sm:text-left">
              <div>
                <p className="text-xs text-muted-foreground">WhatsApp</p>
                <p className="text-base font-bold font-mono">+20 1037530503</p>
              </div>
              <a href="https://wa.me/message/ACL5MCRFCEGOD1" target="_blank" rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full px-4 py-2.5 rounded-xl bg-[#25D366] text-black text-sm font-bold hover:bg-[#1ebe5a] transition-colors">
                <MessageCircle className="h-4 w-4" /> Open WhatsApp
              </a>
              <p className="text-xs text-muted-foreground leading-snug">
                Send screenshot + order number <span className="font-mono font-semibold text-foreground">{orderNumber}</span>
              </p>
            </div>
          </div>
        </div>

        {/* Confirm CTA */}
        <button onClick={handleConfirmed}
          className="w-full flex items-center justify-center gap-2 py-4 rounded-xl bg-primary text-primary-foreground text-sm font-bold hover:bg-primary/90 transition-colors">
          <CheckCircle className="h-4 w-4" /> I've Sent the Payment — Confirm My Order
        </button>
        <p className="text-xs text-center text-muted-foreground">
          Tapping confirm places your order in processing. Shipment begins after payment verification by our team.
        </p>
      </div>
    </div>
  );
}



